/**
 * O que toda tela do painel recebe, num lugar só: quem está logado, com que papel, e como navegar.
 *
 * POR QUE UM CONTEXTO: as oito áreas e a ficha da cliente precisam das mesmas três coisas, e passar
 * por propriedade faria cada tela nova repetir a fiação. Quando a camada de dados (`@studiolu/dados`)
 * entrar, ela entra AQUI, uma vez, e as telas a recebem pelo mesmo gancho.
 */

// Contexto e gancho do React.
import { createContext, useContext } from 'react';
// O tipo da sessão que o provedor de identidade devolve.
import type { Sessao } from '@complexity/auth';
// O acesso lido em `usuarios_sistema` (e-mail e papel).
import type { AcessoAoPainel } from './dados/acesso.js';
// A fonte de dados do pacote `@studiolu/dados`, que toda tela passa aos módulos da sua área.
import type { FonteDeDados } from '@studiolu/dados';

export interface ContextoDoPainel {
  // A sessão do Supabase, com o token que a camada de dados usa.
  readonly sessao: Sessao;
  // A fonte de dados ÚNICA do app (`dados/acesso.ts`): as telas a passam como 1º parâmetro das
  // funções de `@studiolu/dados/areas/<área>`, e nenhuma cria outra.
  readonly dados: FonteDeDados;
  // O e-mail e o papel da pessoa no painel.
  readonly acesso: AcessoAoPainel;
  // A rota atual INTEIRA, com o filtro (`/vendas?etapa=novo`). A tela lê o filtro com
  // `filtroDaRota(rota)` de `rotas.tsx`, para honrar o clique vindo do Painel.
  readonly rota: string;
  // Troca a área útil, como o clique numa aba do admin atual. Ex.: `navegar('/clientes/<id>')`.
  readonly navegar: (rota: string) => void;
}

// Sem valor padrão de propósito: tela fora do painel é defeito, e o gancho acusa.
const Contexto = createContext<ContextoDoPainel | null>(null);

// O provedor, usado uma vez no `App`.
export const ProvedorDoPainel = Contexto.Provider;

// O gancho que as telas usam.
export function usePainel(): ContextoDoPainel {
  const valor = useContext(Contexto);
  // Uma tela montada fora do provedor quebra alto, e não desenha com dado vazio.
  if (!valor) throw new Error('usePainel: tela do painel montada fora do ProvedorDoPainel.');
  return valor;
}
